/**
 * analytics.ts — PostHog wrapper para RHEA (sin dependencia npm)
 * ─────────────────────────────────────────────────────────────────────────────
 * Carga posthog-js desde CDN de forma asíncrona. No requiere npm install.
 * Usa import.meta.env.VITE_POSTHOG_KEY (Vite nativo — 100% confiable).
 * Sin clave → todos los track() son no-ops silenciosos.
 */

// ─── Tipos de eventos ─────────────────────────────────────────────────────────

export interface RheaEvents {
  // Launch Analytics & Funnel Instrumentation V1 — `query` (the literal
  // free-text search) was removed from this event's payload: it can contain
  // arbitrary personal detail a user typed ("viaje con mi hija de 8 años
  // con discapacidad..."), which the privacy rule for this funnel forbids
  // outright. See `experiences_search_submitted` below for the new,
  // privacy-safe canonical version of this event (query_present only).
  search_submitted: {
    has_date:        boolean;
    date_mode?:      "exact" | "flexible" | "none";
    has_children:    boolean;
    children_count?: number;
    travelers:       number;
    source:          "manual" | "teaser" | "inspire";
  };
  teaser_clicked: {
    city:  string;
    query: string;
  };
  sequence_loaded: {
    city:              string;
    iata?:             string;
    days:              number;
    source_class:      string;
    has_provider:      boolean;
    has_availability:  boolean;
    travel_date_mode?: "exact" | "flexible" | "none" | null;
    family_mode:       boolean;
    /**
     * Launch Destination Portfolio V1 — the backend-computed supplier
     * coverage tier for this destination/query (see
     * engine/supply_integration/coverage_tier.py): "strong_provider_supply" |
     * "moderate_provider_supply" | "thin_provider_supply" | "reference_only" |
     * "unsupported". Lets us measure how many real generations land in each
     * tier without re-deriving it client-side. Optional/nullable — omitted
     * on older cached responses that predate this field.
     */
    coverage_tier?:    string | null;
  };
  /**
   * Launch Destination Portfolio V1 — fired once per successful generation
   * whose coverage_tier is "reference_only" (provider_count === 0), so
   * product can measure reference-only impressions distinctly from
   * coming-soon (coming_soon_shown, a different, non-generating case).
   */
  reference_only_result_shown: {
    city: string;
    iata?: string;
  };
  sequence_error: {
    city?:      string;
    error_type: "catalog_unavailable" | "network" | "timeout" | "unknown";
    error_msg?: string;
  };
  // Launch Monitoring & Analytics Dashboard V1 — raw_query removed, same
  // privacy fix as search_submitted/recent_search_clicked above: this could
  // carry arbitrary personal detail a user typed. A prior sprint's own
  // guardrail test explicitly noted this field as "already reviewed" — this
  // sprint's stricter, launch-wide privacy audit supersedes that call.
  coming_soon_shown: {
    city_hint:    string | null;
    query_length: number;
  };
  waitlist_joined: {
    city_hint: string | null;
  };
  experience_expanded: {
    title:         string;
    category?:     string;
    source_class?: string;
    has_booking:   boolean;
    day_number:    number;
    city:          string;
  };
  // Launch Analytics & Funnel Instrumentation V1 — `title` removed: it was
  // sending the full, free-text experience title (a real, if narrower,
  // instance of the same "never a title" rule this funnel's privacy audit
  // enforces everywhere else). `provider_product_clicked`/
  // `marketplace_provider_clicked` already cover the same click with a
  // provider slug and category instead.
  booking_clicked: {
    provider_name?: string;
    city:           string;
    price_from?:    number;
  };
  filter_applied: {
    filter_type: "category" | "duration" | "reservable" | "provider_only" | "time_of_day" | "sort_by" | "max_price" | "include_tip_based";
    value:       string;
    city?:       string;
  };
  date_mode_selected: {
    mode: "exact" | "flexible" | "none";
  };
  travelers_confirmed: {
    adults:        number;
    children:      number;
    traveler_type: string;
  };
  new_search_clicked: {
    from_city?: string;
  };
  // Launch Analytics & Funnel Instrumentation V1 — same privacy fix as
  // search_submitted above: a recent search is still literal free text the
  // user typed. query_length matches the existing intent_parser_fallback
  // convention (length only, never the text itself).
  recent_search_clicked: {
    query_length: number;
  };
  recent_search_removed: {
    query_length: number;
  };
  // Sprint 10 — DayTabs navigation
  day_tab_changed: {
    city?:      string;
    from_day:   number;
    to_day:     number;
    total_days: number;
    direction:  "forward" | "backward" | "jump";
  };
  // Sprint 3E-B — LLM intent parser fell back to regex
  // Only query_length is sent; the raw query is never included.
  intent_parser_fallback: {
    surface:      "experiences" | "planner";
    query_length: number;
  };
  // ── Marketplace Product Detail & Conversion V1 ───────────────────────────
  // Shared safe-metadata shape for every event fired by/about
  // ExperienceDetailDrawer. Deliberately excludes anything free-text or
  // identifying: no description, no booking_url, no user query. `surface`
  // is the UI surface the interaction happened on/from — "detail_drawer"
  // itself for events that occur inside the open drawer (closed, add/
  // remove, provider click), or the card surface that opened it
  // ("featured_shelf" | "marketplace_list" | "selection_tray") for the
  // open event.
  experience_detail_opened:       MarketplaceDetailEventProps;
  experience_detail_closed:       MarketplaceDetailEventProps;
  experience_added_from_detail:   MarketplaceDetailEventProps;
  experience_removed_from_detail: MarketplaceDetailEventProps;
  provider_product_clicked:       MarketplaceDetailEventProps;

  // ── Marketplace Comparison V1 ────────────────────────────────────────────
  // Safe-metadata shape for the comparison feature — never a title,
  // description, or booking_url. `provider`/`category` describe the single
  // item an add/remove/click event is about and are omitted (not
  // fabricated) for a local/reference item or for comparison_opened, which
  // isn't about one item.
  comparison_item_added:      ComparisonEventProps;
  comparison_item_removed:    ComparisonEventProps;
  /** Comparison V2 — a replace (swap one of the 3 slots) is one atomic
   *  action; fired instead of a removed+added pair, never both. */
  comparison_item_replaced:   ComparisonEventProps;
  comparison_opened:          ComparisonEventProps;
  comparison_provider_clicked: ComparisonEventProps;
  /** Comparison V2 — lazy per-item product-detail fetch (cancellation,
   *  languages, group size, pickup, inclusions), fired once per product per
   *  comparator open. Never a title/description/booking_url. */
  comparison_detail_loaded:   ComparisonEventProps;
  comparison_detail_failed:   ComparisonEventProps;

  // ── Marketplace Inventory Scale & Discovery V1 ───────────────────────────
  // Safe-metadata shape for /experiences/search pagination — never the raw
  // query, an experience title, or a URL. filter_count mirrors the same
  // activeFilterCount already computed for the filter UI.
  marketplace_search_completed:   MarketplaceSearchEventProps;
  marketplace_page_loaded:        MarketplaceSearchEventProps;
  marketplace_load_more_clicked:  MarketplaceSearchEventProps;
  marketplace_inventory_exhausted: MarketplaceSearchEventProps;
  /**
   * Supplier Pagination Contract V1 — fired ALONGSIDE (never instead of)
   * marketplace_page_loaded, only for the subset of "cargar más" pages
   * that required a genuine new provider fetch (backend's
   * provider_pagination_used === true), so provider-side pagination usage
   * can be measured distinctly from ordinary local-pool paging. Never the
   * raw query, a title, a booking_url, or a cursor/token.
   */
  marketplace_provider_page_loaded: MarketplaceProviderPageEventProps;

  // ── Launch Analytics & Funnel Instrumentation V1 ─────────────────────────
  // The minimal end-to-end marketplace funnel for launch: search → results →
  // filter/sort → detail/compare → provider click. Every event here is
  // fired ALONGSIDE (never instead of) whichever legacy event already
  // covered the same interaction — see the per-callsite comments for the
  // exact legacy event each one is paired with. None of these ever carries
  // a title, description, booking_url, free-text query, or personal ID;
  // `category`/`provider_name` are the backend's own coarse fields, never
  // free text the user entered.
  experiences_search_submitted: {
    /** Whether a non-empty query was submitted — never the query itself. */
    query_present:      boolean;
    destination_iata?:  string;
    destination_name?:  string;
    adults:             number;
    has_dates:          boolean;
    source_surface:     "search_bar" | "teaser" | "recent_search" | "url_param";
  };
  marketplace_results_loaded: {
    destination_iata?:          string;
    total_results:              number;
    provider_count:             number;
    coverage_tier?:             string | null;
    has_marketplace_inventory:  boolean;
    /** How many interest categories the query/parser detected — not the
     *  query text itself. */
    query_category_count:       number;
  };
  marketplace_filter_applied: {
    filter_type:          "category" | "duration" | "reservable" | "provider_only" | "time_of_day" | "max_price" | "include_tip_based" | "provider" | "service_type" | "filter_taxonomy";
    filter_value:          string;
    active_filter_count:   number;
    result_count_after:    number;
    destination_iata?:     string;
  };
  marketplace_sort_changed: {
    sort_key:           string;
    result_count:       number;
    destination_iata?:  string;
  };
  marketplace_detail_opened:      MarketplaceItemEventProps;
  marketplace_compare_item_added: MarketplaceCompareEventProps;
  marketplace_comparison_opened: {
    destination_iata?:  string;
    comparison_size:    number;
  };
  marketplace_provider_clicked:   MarketplaceProviderClickEventProps;

  // ── Launch Stability & Error Recovery V1 ─────────────────────────────────
  // Fired by LazyPanelBoundary when a lazy-loaded drawer/map fails to load
  // or render. error_message is a JS Error's own .message (a generic string
  // like "Failed to fetch dynamically imported module"), truncated — never
  // a title, description, query, or booking_url, since none of those are
  // ever part of a module-load or render error's message.
  marketplace_panel_error: {
    error_message: string;
  };
}

// ── Launch Analytics & Funnel Instrumentation V1 — shared safe-metadata
// shapes for the new funnel events. `provider_name`/`category` are the
// backend's own coarse fields (a provider slug, a category tag) — never a
// title, description, or free-text query.
export interface MarketplaceItemEventProps {
  destination_iata?: string;
  provider_name?:    string | null;
  category?:         string | null;
  has_price:         boolean;
  has_rating:        boolean;
  has_booking_url:   boolean;
}

export interface MarketplaceCompareEventProps {
  destination_iata?: string;
  provider_name?:    string | null;
  category?:         string | null;
  comparison_size:   number;
}

export type MarketplaceProviderClickSurface = MarketplaceDetailSurface | "comparison_drawer";

// Provider Landing Accuracy + Query Expansion Prep V1 — which booking_url
// shape actually reached the anchor's href. Every current click is
// "current_affiliate" (the audit found no variant that lands more
// specifically than this one — see scripts/provider_landing_accuracy_
// audit.py); the other values exist so a future landing-policy change can
// be measured without another instrumentation sprint.
export type MarketplaceLinkVariant =
  | "current_affiliate"
  | "raw_product_url"
  | "clean_canonical"
  | "detail_product_url";

export interface MarketplaceProviderClickEventProps {
  destination_iata?: string;
  provider_name?:    string | null;
  category?:         string | null;
  surface:           MarketplaceProviderClickSurface;
  has_price:         boolean;
  has_rating:        boolean;
  has_booking_url:   boolean;
  link_variant:      MarketplaceLinkVariant;
}

export interface ComparisonEventProps {
  destination_iata?: string;
  provider?:         string | null;
  category?:         string | null;
  comparison_size?:  number;
}

export interface MarketplaceSearchEventProps {
  destination_iata?: string;
  page_size?:        number;
  returned_count?:   number;
  provider_count?:   number;
  filter_count?:     number;
  sort?:             string;
  has_more?:         boolean;
}

/** Supplier Pagination Contract V1 — see marketplace_provider_page_loaded above. */
export interface MarketplaceProviderPageEventProps {
  destination_iata: string;
  provider:         string;
  page_number:      number;
  items_returned:   number;
  has_more:         boolean;
}

/**
 * Hotfix: Provider Pagination Context Parity + Multi-Provider Analytics V1.
 *
 * Minimal shape this builder needs from a /experiences/search response —
 * a subset of MarketplaceSearchResponse, kept separate here so this file
 * never has to import the full response type.
 */
export interface ProviderPageLoadedSource {
  provider_pagination_used?: boolean;
  provider_pages?: Array<{ provider: string; page_number: number }>;
  provider_page?: { provider: string; page_number: number } | null;
  returned_count: number;
  has_more: boolean;
}

/**
 * Pure decision function for how many marketplace_provider_page_loaded
 * events a given /experiences/search response should produce — extracted
 * so the emission COUNT logic (the actual bug this hotfix closes: two
 * providers pagination in one action used to produce ZERO events) is
 * directly unit-testable without a DOM.
 *
 * Rules:
 *   - provider_pagination_used must be true, or nothing is emitted.
 *   - provider_pages (new, additive) is the source of truth once the
 *     backend sends it: one event per entry, even when it has only one
 *     (never ALSO falls through to provider_page in that case — that
 *     would double-count the single-provider case).
 *   - provider_page (legacy singular) is used ONLY as a fallback when
 *     provider_pages is absent or empty (an older backend, or — per the
 *     current backend contract — a "cargar más" that queried zero
 *     providers this round, which the caller side never reaches anyway
 *     since provider_pagination_used would be false in that case).
 */
export function buildProviderPageLoadedEvents(
  result:           ProviderPageLoadedSource,
  destinationIata:  string,
): MarketplaceProviderPageEventProps[] {
  if (!result.provider_pagination_used) return [];

  const base = {
    destination_iata: destinationIata,
    items_returned:   result.returned_count,
    has_more:         result.has_more,
  };

  if (result.provider_pages && result.provider_pages.length > 0) {
    return result.provider_pages.map(page => ({
      ...base,
      provider:    page.provider,
      page_number: page.page_number,
    }));
  }

  if (result.provider_page) {
    return [{
      ...base,
      provider:    result.provider_page.provider,
      page_number: result.provider_page.page_number,
    }];
  }

  return [];
}

export type MarketplaceDetailSurface =
  | "featured_shelf"
  | "marketplace_list"
  | "selection_tray"
  | "detail_drawer";

export interface MarketplaceDetailEventProps {
  experience_id:                string;
  iata?:                        string;
  source_type?:                 string | null;
  provider_name?:               string | null;
  marketplace_relevance_class?: string | null;
  surface:                      MarketplaceDetailSurface;
}

// ─── Interfaz mínima de PostHog (evita import de tipos npm) ──────────────────

interface PostHogInstance {
  capture:  (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
  reset:    () => void;
}

// ─── Inicialización via CDN ───────────────────────────────────────────────────

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

// Pre-load event queue — capped at 100 to avoid unbounded growth on CDN failure
const MAX_QUEUE = 100;
const _queue: Array<[string, Record<string, unknown>]> = [];
let _ph:      PostHogInstance | null = null;
let _disabled = false;

function _disable(): void {
  _disabled     = true;
  _ph           = null;
  _queue.length = 0;
}

if (KEY) {
  const script       = document.createElement("script");
  script.src         = "https://us-assets.i.posthog.com/static/array.js";
  script.async       = true;
  script.crossOrigin = "anonymous";

  script.onload = () => {
    const ph = (window as unknown as Record<string, unknown>).posthog as PostHogInstance | undefined;

    if (!ph) {
      // Script loaded but window.posthog is absent — disable for this session.
      // Note: a 404 on PostHog's remote config.js is a separate network request
      // and does NOT reliably trigger script.onerror; verify in the dashboard.
      _disable();
      return;
    }

    try {
      (ph as unknown as { init: (k: string, opts: object) => void }).init(KEY, {
        api_host:          "https://us.i.posthog.com",
        capture_pageview:  true,
        capture_pageleave: true,
        autocapture:       false,
        persistence:       "localStorage+cookie",
      });

      _ph = ph;

      // Only drain after init succeeds
      for (const [event, props] of _queue) {
        try { ph.capture(event, props); } catch { /* silent */ }
      }
      _queue.length = 0;
    } catch {
      _disable();
    }
  };

  script.onerror = () => {
    // CDN script fetch failed — disable analytics for this session.
    _disable();
  };

  document.head.appendChild(script);
}

// ─── API pública ─────────────────────────────────────────────────────────────

export function track<K extends keyof RheaEvents>(
  event: K,
  properties: RheaEvents[K],
): void {
  if (!KEY || _disabled) return;
  const props = properties as Record<string, unknown>;
  try {
    if (_ph) {
      _ph.capture(event, props);
    } else if (_queue.length < MAX_QUEUE) {
      _queue.push([event, props]);
    }
    // If queue is full, silently drop — CDN has not loaded after 100 events.
  } catch { /* silent */ }
}

export function identifyUser(userId: string, props?: {
  email?: string;
  created_at?: string;
}): void {
  if (!KEY || _disabled) return;
  try { _ph?.identify(userId, props); } catch { /* silent */ }
}

export function resetAnalytics(): void {
  if (!KEY || _disabled) return;
  try { _ph?.reset(); } catch { /* silent */ }
}
